import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createQueryClient } from "./query-client";
import { BrowserRouter } from "react-router-dom";
import {
  PublicClientApplication,
  EventType,
  EventMessage,
  AuthenticationResult,
  BrowserUtils,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { GoogleOAuthProvider } from "@react-oauth/google";
import App from "./App";
import "./index.css";
import "./i18n";
import { msalConfig } from "./config/msal-config";
import { googleConfig } from "./config/google-config";
import { initAppInsights } from "./config/app-insights";
import { registerServiceWorker } from "./offline/update";

// Initialize Application Insights for client-side telemetry
initAppInsights();

// Keep the app files on the device so the app opens without a connection
void registerServiceWorker();

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Initialize MSAL and render the app
 * MSAL must be fully initialized before rendering to avoid popup issues
 */
async function initializeAndRender() {
  // Wait for MSAL to fully initialize
  await msalInstance.initialize();

  // Check if this is a popup window - if so, don't render the full app
  // The popup just needs to handle the redirect and close
  if (BrowserUtils.isInPopup()) {
    console.log("Running in popup - handling redirect only");
    try {
      const result = await msalInstance.handleRedirectPromise();
      console.log("Popup handleRedirectPromise completed, result:", !!result);

      // Signal parent that we're done and close after a brief delay to ensure parent processed it
      if (window.opener && !window.opener.closed) {
        console.log("Signaling parent window and closing popup");
        // Give parent window a moment to capture the event before closing
        await new Promise((resolve) => setTimeout(resolve, 200));
        window.close();
      } else {
        console.log("No parent window or already closed, closing popup");
        window.close();
      }
    } catch (error) {
      console.error("Popup redirect error:", error);
      // Close popup even if there's an error
      setTimeout(() => {
        window.close();
      }, 500);
    }
    // Don't render the full app in popup
    return;
  }

  // Handle redirect response if returning from Microsoft login (non-popup)
  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response) {
      console.log("Redirect login successful:", response.account?.username);
      msalInstance.setActiveAccount(response.account);
      // Store token info for the app to pick up
      sessionStorage.setItem(
        "msal_redirect_result",
        JSON.stringify({
          accessToken: response.accessToken,
          account: response.account,
        }),
      );
    }
  } catch (error) {
    console.error("Redirect login error:", error);
    // Clear any stale interaction state on error
    sessionStorage.removeItem("msal.interaction.status");
  }

  // Account selection logic is app dependent. Adjust as needed for different use cases.
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    msalInstance.setActiveAccount(accounts[0]);
  }

  msalInstance.addEventCallback((event: EventMessage) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      const account = payload.account;
      msalInstance.setActiveAccount(account);
    }
    // Handle login failure - clear stale state
    if (event.eventType === EventType.LOGIN_FAILURE) {
      console.warn("Login failed, clearing interaction state");
      sessionStorage.removeItem("msal.interaction.status");
    }
  });

  const queryClient = createQueryClient();

  // Export queryClient for use in other parts of the app (e.g., clearing cache on logout)
  (window as unknown as { queryClient: QueryClient }).queryClient = queryClient;

  ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <GoogleOAuthProvider clientId={googleConfig.clientId}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </QueryClientProvider>
        </GoogleOAuthProvider>
      </MsalProvider>
    </React.StrictMode>,
  );
}

// Start initialization
initializeAndRender().catch(console.error);
